import * as React from 'react';

import { useLocation, useParams, useRoutes } from 'js/lib/useRouter';

import type { UniversityProgramDeadlines } from 'bundles/degree-description/__generated__/baseContentfulTypes';
import EoiInlineModule from 'bundles/degree-description/components/modules/expression-of-interest/EoiInlineModule';
import FaqModule from 'bundles/degree-description/components/modules/faqs/FaqModule';
import AtAGlanceHorizontalBlock from 'bundles/degree-description/components/modules/hero/AtAGlanceHorizontalBlock';
import DeadlinesBlock from 'bundles/degree-description/components/modules/hero/DeadlinesBlock';
import EoiButtonsBlock from 'bundles/degree-description/components/modules/hero/EoiButtonsBlock';
import HeroBanner from 'bundles/degree-description/components/modules/hero/HeroBanner';
import HeroModule from 'bundles/degree-description/components/modules/hero/HeroModule';
import InformationalSectionContainer from 'bundles/degree-description/components/modules/hero/InformationalSectionContainer';
import InfoSectionModule from 'bundles/degree-description/components/modules/info-section/InfoSectionModule';
import PersonalizedEmbeddedMsg from 'bundles/degree-description/components/modules/personalization/PersonalizedEmbeddedMsg';
import SchemaMarkup from 'bundles/degree-description/components/modules/structured-data-markup/SchemaMarkup';
import { HideOnDesktop, HideOnMobile } from 'bundles/degree-description/components/shared/BreakpointDisplay';
import ModuleContainerStyleWrap from 'bundles/degree-description/components/shared/ModuleContainerStyleWrap';
import InlineNavigation from 'bundles/degree-description/components/shared/Navigation/InlineNavigation';
import RichText from 'bundles/degree-description/components/shared/RichText';
import type { PropsFromDegreePageContent } from 'bundles/degree-description/pages/consumer/degree-description-page/DegreePageContent';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import { PAGE_NAMES_MARKUP } from 'bundles/degree-description/utils/schema-markup';
import { getNavigationItems } from 'bundles/degree-description/utils/utils';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';

import _t from 'i18n!nls/degree-description';

const AcademicsPageContent = ({
  pageContent,
  eoiButtons,
  eoiContainerText,
  setEoiTrackingIndex,
  personalizedDdpData,
  stackableItemsWithUpsellType,
}: PropsFromDegreePageContent) => {
  const {
    academicsAbout,
    backgroundImage,
    partner,
    programName,
    academicsValueProp,
    atAGlance,
    academicBlocksCollection,
    deadlineSectionCollection,
  } = pageContent;

  // Page Sections
  const valuePropText = academicsValueProp?.json;
  const valuePropImage = academicsValueProp?.links?.assets?.block?.[0];

  const location = useLocation();
  const { slug } = useParams();
  const routes = useRoutes();

  // Page Section Tracking
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageHeaderTrackingRef = useGetViewPageSectionRef('page_header');
  const pageBodyTrackingRef = useGetViewPageSectionRef('page_body');

  // Personalization
  const messagesToDisplay = getMessagesToDisplay(
    personalizedDdpData,
    slug,
    programName,
    partner?.partnerName,
    deadlineSectionCollection?.items as UniversityProgramDeadlines[]
  );

  return (
    <div>
      <SchemaMarkup
        pageName={PAGE_NAMES_MARKUP.academics}
        pathName={location.pathname}
        pageContent={pageContent}
        stackableItemsWithUpsellType={stackableItemsWithUpsellType}
      />
      <HeroModule
        informationalSection={
          <InformationalSectionContainer
            content={
              academicsAbout?.json && (
                <div ref={pageHeaderTrackingRef}>
                  <RichText
                    content={academicsAbout?.json}
                    contentLinks={academicsAbout?.links}
                    linkTrackingName="academicsAbout_body_link"
                    buttonTrackingNameV3="degree_info_link"
                    trackingSection="page_header"
                  />
                </div>
              )
            }
            sidebarContent={
              <HideOnMobile>
                <DeadlinesBlock finalDeadlineInfo={atAGlance?.finalDeadlineInfo} />
              </HideOnMobile>
            }
          />
        }
        heroBanner={
          <HeroBanner
            backgroundImageUrl={backgroundImage?.url ?? ''}
            partnerLogoUrl={partner?.partnerLogo?.url ?? ''}
            partnerName={partner?.partnerName ?? ''}
            programName={programName ?? ''}
            sidebarContent={
              <EoiButtonsBlock
                eoiContainerText={eoiContainerText}
                eoiButtons={eoiButtons}
                trackingIndex={1}
                setEoiTrackingIndex={setEoiTrackingIndex}
              />
            }
          />
        }
        contentInBetween={
          <>
            <AtAGlanceHorizontalBlock atAGlance={atAGlance} />
            <InlineNavigation
              navigationItems={getNavigationItems()}
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              parentNavRoute={routes[routes.length - 2].name}
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              currentNavRoute={routes[routes.length - 1].name}
              // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              slug={slug}
            />
          </>
        }
      />
      <div ref={pageBodyTrackingRef}>
        <HideOnDesktop>
          <>
            {messagesToDisplay.embeddedMessageToDisplay && (
              <ModuleContainerStyleWrap blueBackground>
                <PersonalizedEmbeddedMsg
                  personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                  eoiButtons={eoiButtons}
                  setEoiTrackingIndex={setEoiTrackingIndex}
                />
              </ModuleContainerStyleWrap>
            )}
            <DeadlinesBlock finalDeadlineInfo={atAGlance?.finalDeadlineInfo} />
          </>
        </HideOnDesktop>
        <HideOnMobile>
          <>
            {messagesToDisplay.embeddedMessageToDisplay && (
              <ModuleContainerStyleWrap blueBackground>
                <PersonalizedEmbeddedMsg
                  personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                  eoiButtons={eoiButtons}
                  setEoiTrackingIndex={setEoiTrackingIndex}
                />
              </ModuleContainerStyleWrap>
            )}
          </>
        </HideOnMobile>

        {valuePropText && valuePropImage && valuePropImage?.url && valuePropImage?.width && valuePropImage?.height && (
          <ModuleContainerStyleWrap blueBackground>
            <InfoSectionModule
              textColor="var(--cds-color-neutral-primary-invert)"
              description={
                <RichText
                  content={valuePropText}
                  linkTrackingName="academicsValueProp_info_link"
                  buttonTrackingNameV3="degree_info_link"
                  trackingSection="value_proposition"
                />
              }
              media={
                <LazyImgix
                  src={valuePropImage.url}
                  maxWidth={valuePropImage.width}
                  maxHeight={valuePropImage.height}
                  alt={valuePropImage.description || ''}
                  enableFluidWidth={true}
                />
              }
            />
          </ModuleContainerStyleWrap>
        )}
        {academicBlocksCollection?.items && academicBlocksCollection?.items.length > 0 && (
          <ModuleContainerStyleWrap>
            <FaqModule
              header={_t('Curriculum')}
              faqItems={academicBlocksCollection.items}
              accordionTrackingName="curriculum"
              accordionEventingV3TrackingName="admission_item"
              linkTrackingName="curriculum_link"
            />
          </ModuleContainerStyleWrap>
        )}
        <HideOnMobile>
          <EoiInlineModule
            eoiContainerText={eoiContainerText}
            eoiButtons={eoiButtons}
            trackingIndex={2}
            setEoiTrackingIndex={setEoiTrackingIndex}
          />
        </HideOnMobile>
      </div>
    </div>
  );
};

export default AcademicsPageContent;
